import React, { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { isLocked } from './config';
import { collectTopics, nodePct, videoCompleted } from './data/progress';

/**
 * LexicalResults — everything below the Pro+ panel on the search results page.
 *
 *   headline  →  hero columns (one per group with results) + comet trails
 *             →  segmented tabs  →  the selected tab's results
 *   desktop: a compact bar slides in once the tabs scroll out of view.
 *
 * LOCKS. Every lock reads the node's own `tierRequired` from the hierarchy
 * (entries carry it; lessons read `topic.tierRequired`). Nothing here derives a
 * tier from another node. Courses and chapters always navigate; sections only
 * expand; only locked topics open the upgrade prompt (via onOpenTopic / the page).
 *
 * TAB STATE lives only in this component. The page mounts it with key={query},
 * so every new search starts fresh. Nothing goes in the URL or history.
 */

// Display order of the groups (columns, trails, tabs, bar): narrowest → broadest.
export const TAB_ORDER = ['topics', 'sections', 'chapters', 'courses'];
// Which tab opens first: the first group here that has results. Kept separate from
// TAB_ORDER on purpose, so each can be changed on its own.
export const TAB_PRIORITY = ['topics', 'sections', 'chapters', 'courses'];

const TABS = {
  topics:   { label: 'Topics',   desc: 'Single videos' },
  sections: { label: 'Sections', desc: 'Groups of lessons' },
  chapters: { label: 'Chapters', desc: 'Units in a course' },
  courses:  { label: 'Courses',  desc: 'Full courses' },
};
const PAGE = 24;           // cards / rows rendered per tab before "Show more"
const SIDEBAR_W = 280;     // AppLayout's desktop sidebar width
const BAR_STRIP_TOP = 72;  // the bar's tab strip starts below the sidebar toggle (28px + 40px tall)
// MOTION. Luxurious, not snappy: decisive starts, long soft landings, small
// distances, and things arriving one after another. GLIDE is an ease-out-quint
// already used on the site; colour and light changes use a plain, gentle ease.
const GLIDE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const MOTION = {
  pill: 700,        // tab highlight glide
  label: 500,       // tab label colour
  fade: 600,        // hero numbers / labels / trail light
  lightTravel: 450, // time for light to run down a newly selected trail
  // Switching tabs is a FADE THROUGH (Material's pattern for unrelated content): the
  // old results leave completely, a brief breath, then the new results arrive — never
  // both on screen at once. Leaving is short and accelerating; arriving is long and
  // soft, so attention lands on what's new.
  out: 280,         // old results fade out + drift 8px away (accelerating)
  breath: 40,       // empty pause between them (layout held still)
  in: 780,          // new results fade in + settle from 98.5% scale and 12px
  item: 800,        // each card / row settling
  stagger: 70,      // gap between cards in a cascade
  staggerMax: 8,    // cascade at most this many; the rest arrive with the 8th
  bar: 600,         // collapse bar glide in / out
  expand: 600,      // section opening
};
// Opening sequence when results first appear (ms from mount). Set false to skip it.
const INTRO = true;
const INTRO_AT = { headline: 0, trails: 200, columns: 520, columnStep: 70, drops: 700, tabs: 900, results: 1000 };

// Sizes above the tabs. Kept together so the hero can be tuned in one place.
const SIZES = {
  desktop: { headline: 52, headTop: 16, trailH: 90, num: 68, label: 12, labelGap: 10, desc: 14, dropH: 40, dropGap: 12, tabH: 50, tabFont: 14, icon: 17 },
  tablet:  { headline: 44, headTop: 12, trailH: 80, num: 58, label: 11, labelGap: 8,  desc: 13, dropH: 36, dropGap: 10, tabH: 48, tabFont: 14, icon: 16 },
  mobile:  { headline: 30, headTop: 6,  trailH: 60, num: 34, label: 10, labelGap: 6,  desc: 0,  dropH: 28, dropGap: 8,  tabH: 54, tabFont: 12, icon: 16 },
};

const TAB_ICON = {
  topics:   <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M10 9l5 3-5 3z" /></>,
  sections: <><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" /></>,
  chapters: <path d="M4 6h16M4 12h16M4 18h10" />,
  courses:  <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /></>,
};
const Icon = ({ kind, color, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {TAB_ICON[kind]}
  </svg>
);

const LockBadge = ({ color, bg }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: bg, color, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
    Locked
  </span>
);

// THE PAGE'S SCROLLER. On this site the <body> scrolls, not the window
// (App.jsx: html, body { height: 100%; overflow-x: hidden }), so window.scrollY
// stays 0 and window.scrollTo does nothing. Use whichever of the standard scrolling
// element and <body> is actually scrollable; fall back to the standard one. Works on
// a conventional window-scrolling setup too.
function pageScroller() {
  const std = document.scrollingElement || document.documentElement;
  for (const el of [std, document.body]) {
    if (!el) continue;
    const oy = getComputedStyle(el).overflowY;
    if (el.scrollHeight > el.clientHeight + 1 && (el === std || oy === 'auto' || oy === 'scroll')) return el;
  }
  return std;
}
// Top of the scroller's visible area, in viewport coordinates (0 for the window).
const scrollerTop = (sc) => (sc === (document.scrollingElement || document.documentElement) ? 0 : sc.getBoundingClientRect().top);
// Scroll events from <body> don't bubble to window — listen in the capture phase.
const SCROLL_OPTS = { capture: true, passive: true };

// Keep the user's reduced-motion preference in one place.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ── Comet trails ────────────────────────────────────────────────────────────
// A trail of dots that grow and brighten toward its target, ending in a slim
// notched arrowhead. The selected group's trail uses the accent colour and its
// arrowhead has a soft halo (a larger, faint copy behind it — cheaper to paint than
// an SVG blur filter).
function trailMarks(x0, y0, x1, y1, on, colors, scale, introAt = 0) {
  const dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len, ang = (Math.atan2(uy, ux) * 180) / Math.PI;
  const col = on ? colors.accent : colors.muted;
  const n = Math.max(3, Math.floor(len / (13 * scale)));
  const dots = [];
  for (let k = 0; k < n; k++) {
    const t = (k + 0.5) / (n + 1);
    // When this path becomes selected, each dot brightens a little after the one
    // before it, so light appears to run from the origin to the arrowhead. When it
    // is deselected, every dot dims together.
    dots.push(<circle key={k} className="lx-fade lx-dot" cx={x0 + dx * t} cy={y0 + dy * t} r={(0.7 + 1.9 * t) * scale}
      fill={col} opacity={(0.12 + 0.88 * Math.pow(t, 1.5)) * (on ? 1 : 0.5)}
      style={{ transitionDelay: on ? Math.round((k / n) * MOTION.lightTravel) + 'ms' : '0ms', '--d': Math.round(introAt + (k / n) * 250) + 'ms' }} />);
  }
  const s = 4.6 * scale, L = 2.1 * s, W = 0.95 * s;
  const head = `M0 0 L${-L} ${-W} L${-L * 0.62} 0 L${-L} ${W} Z`;
  return (
    <g>
      {dots}
      {/* the halo is always drawn (faded out when not selected) so it fades rather than pops */}
      <path className="lx-fade lx-dot" d={head} transform={`translate(${x1} ${y1}) rotate(${ang}) scale(1.9)`} fill={colors.accent} opacity={on ? 0.22 : 0} data-halo={on ? '1' : undefined}
        style={{ transitionDelay: on ? MOTION.lightTravel + 'ms' : '0ms', '--d': (introAt + 280) + 'ms' }} />
      <path className="lx-fade lx-dot" d={head} transform={`translate(${x1} ${y1}) rotate(${ang})`} fill={col} opacity={on ? 1 : 0.55}
        style={{ transitionDelay: on ? Math.round(MOTION.lightTravel * 0.9) + 'ms' : '0ms', '--d': (introAt + 260) + 'ms' }} />
    </g>
  );
}

// Headline → columns. Origins sit along a faint curve under the headline, each 40%
// of the way from the centre toward its column, and every trail aims at the centre
// of its column's number. Targets are MEASURED ({ x, y }, in the columns' own
// coordinates; the svg sits directly on top of the columns and shares their width),
// so the aim stays exact at any width or font size.
function HeadlineTrails({ width, targets, activeIdx, colors, height, scale }) {
  const glowId = 'lx-glow-' + useId().replace(/[^a-zA-Z0-9_-]/g, '');
  if (!width || !targets.length) return <div style={{ height }} />;
  const centers = targets.map((t) => t.x);
  const x0 = width / 2, oy = 10, y1 = height - 6;
  const half = Math.max(40, (Math.max(...centers.map((c) => Math.abs(c - x0))) * 0.4) + 60);
  const arcY = (x) => oy + 6 * (1 - Math.pow((x - x0) / half, 2));
  const arcPts = Array.from({ length: 31 }, (_, k) => { const x = x0 - half + (2 * half * k) / 30; return `${x.toFixed(1)},${arcY(x).toFixed(1)}`; }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={glowId + '-hz'} gradientUnits="userSpaceOnUse" x1={x0 - half} y1="0" x2={x0 + half} y2="0">
          <stop offset="0" stopColor={colors.muted} stopOpacity="0" />
          <stop offset="0.5" stopColor={colors.muted} stopOpacity="0.35" />
          <stop offset="1" stopColor={colors.muted} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={arcPts} fill="none" stroke={`url(#${glowId}-hz)`} strokeWidth="1" />
      {targets.map(({ x: cx, y }, i) => {
        const ox = x0 + (cx - x0) * 0.4, oyy = arcY(ox);
        const ty = height + y;   // the number's centre, below the svg
        // point where the line from the origin toward (cx, ty) meets the svg's bottom
        const t = (y1 - oyy) / (ty - oyy);
        return <g key={i}>{trailMarks(ox, oyy, ox + (cx - ox) * t, y1, i === activeIdx, colors, scale, INTRO_AT.trails)}</g>;
      })}
    </svg>
  );
}

// Column → tab: a short vertical trail in the same style.
function DropTrail({ on, colors, height, scale }) {
  return (
    <svg width="24" height={height} viewBox={`0 0 24 ${height}`} aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      {trailMarks(12, 2, 12, height - 2, on, colors, scale, INTRO_AT.drops)}
    </svg>
  );
}

// ── Result cards ────────────────────────────────────────────────────────────
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function Progress({ pct, colors, isLight, width = 120 }) {
  return (
    <div style={{ width, maxWidth: '100%', height: 4, borderRadius: 2, background: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)', flexShrink: 0 }}>
      <div style={{ width: pct + '%', height: 4, borderRadius: 2, background: colors.accent }} />
    </div>
  );
}

function CourseCard({ entry, colors, isLight, isMobile, onGo, progressByVideo, lockStyle }) {
  const node = entry.node || {};
  const pct = nodePct(node, progressByVideo);
  const chapters = (node.chapters || []).length;
  const lessons = collectTopics(node).length;
  const size = isMobile ? 92 : 112, r = size / 2 - 3, circ = 2 * Math.PI * r;
  return (
    <button type="button" className="lx-card" onClick={() => onGo(entry.path)} style={{
      width: '100%', height: '100%', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '10px',
      padding: isMobile ? '22px 16px' : '28px 20px', borderRadius: '18px', cursor: 'pointer', font: 'inherit',
      background: colors.cardBg || colors.bgAlt, border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`, color: colors.text,
    }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" style={{ position: 'absolute', inset: 0 }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)'} strokeWidth="3" />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colors.accent} strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${(circ * pct) / 100} ${circ}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        </svg>
        <div style={{ position: 'absolute', inset: 9, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `radial-gradient(circle at 35% 30%, ${colors.accentGlow || colors.accent}, ${colors.accent})`, opacity: entry.locked ? 0.55 : 1 }}>
          <span style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontWeight: 600, fontSize: size * 0.32, color: isLight ? '#FFFFFF' : '#1A1A1A' }}>{entry.symbol || ''}</span>
        </div>
      </div>
      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.muted }}>{entry.crumb}</div>
      <div style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: isMobile ? '24px' : '28px', lineHeight: 1.1 }}>{entry.title}</div>
      <div style={{ fontSize: '13px', color: colors.textSecondary || colors.muted }}>{plural(chapters, 'chapter', 'chapters')} · {plural(lessons, 'lesson', 'lessons')}</div>
      {entry.locked && <LockBadge {...lockStyle} />}
    </button>
  );
}

function ChapterCard({ entry, colors, isLight, isMobile, onGo, progressByVideo, lockStyle }) {
  const node = entry.node || {};
  const topics = collectTopics(node);
  const pct = nodePct(node, progressByVideo);
  const thumbs = topics.slice(0, isMobile ? 2 : 4);
  return (
    <button type="button" className="lx-card" onClick={() => onGo(entry.path)} style={{
      display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '12px' : '24px',
      padding: isMobile ? '16px' : '18px 22px', borderRadius: '16px', cursor: 'pointer', font: 'inherit', textAlign: 'left', width: '100%',
      background: colors.cardBg || colors.bgAlt, border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`, color: colors.text,
    }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.muted }}>{entry.crumb}</div>
        <div style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: isMobile ? '22px' : '26px', lineHeight: 1.1 }}>{entry.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: colors.textSecondary || colors.muted }}>{plural((node.sections || []).length, 'section', 'sections')} · {plural(topics.length, 'lesson', 'lessons')}</span>
          {topics.length > 0 && <Progress pct={pct} colors={colors} isLight={isLight} />}
          {entry.locked && <LockBadge {...lockStyle} />}
        </div>
      </div>
      {thumbs.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {thumbs.map((t) => (
            <div key={t.videoId || t.id} style={{ width: isMobile ? 120 : 112, height: isMobile ? 68 : 63, borderRadius: '8px', overflow: 'hidden', background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)' }}>
              {t.thumbnailUrl && <img src={t.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

function SectionRow({ entry, colors, isLight, isMobile, progressByVideo, currentTier, onOpenTopic, lockStyle }) {
  const [open, setOpen] = useState(false);
  // Lessons are built the first time a row opens (not for every row up front, which
  // made switching to a long Sections list heavy), then kept so closing animates too.
  const [built, setBuilt] = useState(false);
  const toggle = () => { setBuilt(true); setOpen((o) => !o); };
  const node = entry.node || {};
  const topics = node.topics || [];
  const pct = nodePct(node, progressByVideo);
  const edge = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  return (
    <div style={{ borderRadius: '14px', background: colors.cardBg || colors.bgAlt, border: `1px solid ${open ? colors.accent : edge}`, overflow: 'hidden', transition: `border-color ${MOTION.expand}ms ease` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', padding: isMobile ? '12px 14px' : '14px 18px' }}>
        <button type="button" onClick={toggle} aria-expanded={open} aria-label={(open ? 'Hide' : 'Show') + ' lessons in ' + entry.title}
          style={{ width: 34, height: 34, flexShrink: 0, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: `1px solid ${edge}` }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={colors.textSecondary || colors.muted} strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: `transform 500ms ${GLIDE}` }}><path d="M9 6l6 6-6 6" /></svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 600, color: colors.text }}>{entry.title}</div>
          <div style={{ fontSize: '12.5px', color: colors.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.crumb}</div>
        </div>
        {!isMobile && <span style={{ fontSize: '13px', color: colors.textSecondary || colors.muted, flexShrink: 0 }}>{plural(topics.length, 'lesson', 'lessons')}</span>}
        {!isMobile && topics.length > 0 && <Progress pct={pct} colors={colors} isLight={isLight} width={100} />}
        {entry.locked && <LockBadge {...lockStyle} />}
      </div>
      {/* The lessons are always rendered; the row opens and closes by animating its
          height (grid rows 0fr ↔ 1fr), and hidden lessons are invisible to clicks,
          the Tab key and screen readers. */}
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: `grid-template-rows ${MOTION.expand}ms ${GLIDE}, visibility 0s linear ${open ? '0ms' : MOTION.expand + 'ms'}`, visibility: open ? 'visible' : 'hidden' }} aria-hidden={!open}>
       <div style={{ overflow: 'hidden', minHeight: 0 }}>
        <div className={open ? 'lx-lessons-open' : undefined} style={{ padding: isMobile ? '0 10px 10px' : '0 18px 14px 68px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {built && topics.length === 0 && <div style={{ fontSize: '13px', color: colors.muted, padding: '6px 10px' }}>No lessons in this section yet.</div>}
          {built && topics.map((t, li) => {
            const locked = isLocked(t.tierRequired, currentTier);   // the topic's own tier
            const watched = videoCompleted(progressByVideo, t.videoId);
            return (
              <button key={t.videoId || t.id} type="button" tabIndex={open ? 0 : -1} onClick={() => onOpenTopic({ videoId: t.videoId, locked }, entry.courseId)}
                className="lx-lesson" style={{ '--i': (120 + Math.min(li, MOTION.staggerMax) * 55) + 'ms', display: 'flex', alignItems: 'center', gap: '12px', padding: '7px 10px', borderRadius: '10px', cursor: 'pointer', font: 'inherit', textAlign: 'left', background: 'transparent', border: 0, color: colors.text }}>
                <div style={{ width: 64, height: 36, borderRadius: '6px', overflow: 'hidden', flexShrink: 0, background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)' }}>
                  {t.thumbnailUrl && <img src={t.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <span style={{ flex: 1, minWidth: 0, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name || 'Untitled'}</span>
                {watched && <span style={{ fontSize: '12.5px', color: colors.accent, flexShrink: 0 }}>✓ Watched</span>}
                {locked && <LockBadge {...lockStyle} />}
              </button>
            );
          })}
        </div>
       </div>
      </div>
    </div>
  );
}

// ── Tab strip ───────────────────────────────────────────────────────────────
// One segmented strip with a single highlight pill that slides under the selected
// tab (position + width, the site's easing). Used twice: on the page, and in the
// desktop collapse bar. On first placement the pill appears in place — it only
// slides once it has somewhere to slide from.
function TabStrip({ keys, active, counts, compact, size, colors, isLight, edge, onPick, onKeyDown, stripRef, isMobile }) {
  const innerRef = useRef(null);
  const keysKey = keys.join(',');   // a plain value for the hook's dependency list
  // The pill SLIDES only when the selected tab changes. Any other move — first
  // placement, window resize, scrollbar appearing, sidebar opening, fonts landing —
  // SNAPS, so the highlight never drifts into place on its own.
  const [pill, setPill] = useState(null);   // { x, y, w, h, slide }
  const lastActive = useRef(null);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return undefined;
    const place = (slide) => {
      const btn = el.querySelector(`[data-key="${active}"]`);
      if (!btn) return;
      const next = { x: btn.offsetLeft, y: btn.offsetTop, w: btn.offsetWidth, h: btn.offsetHeight, slide };
      setPill((p) => (p && p.x === next.x && p.y === next.y && p.w === next.w && p.h === next.h ? p : next));
    };
    // a real selection change slides (but never the very first placement)
    place(lastActive.current !== null && lastActive.current !== active);
    lastActive.current = active;
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => place(false));
    ro.observe(el);
    return () => ro.disconnect();
  }, [active, keysKey]);

  const column = isMobile && !compact;
  return (
    <div
      ref={(el) => { innerRef.current = el; if (stripRef) stripRef.current = el; }}
      role="tablist" aria-label="Result groups" onKeyDown={onKeyDown}
      style={{ position: 'relative', display: 'flex', gap: '6px', padding: '6px', borderRadius: '16px', background: colors.bgAlt || colors.bg, border: `1px solid ${edge}` }}>
      {pill && (
        <div aria-hidden="true" className="lx-pill" style={{
          position: 'absolute', left: 0, top: 0, width: pill.w, height: pill.h,
          transform: `translate(${pill.x}px, ${pill.y}px)`, borderRadius: '12px',
          background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.07)', boxShadow: `0 0 0 1px ${colors.accent}`,
          transition: pill.slide ? `transform ${MOTION.pill}ms ${GLIDE}, width ${MOTION.pill}ms ${GLIDE}` : 'none',
        }} />
      )}
      {keys.map((k) => {
        const on = k === active;
        return (
          <button key={k} type="button" role="tab" aria-selected={on} data-key={k} data-tab={compact ? undefined : k} tabIndex={on ? 0 : -1}
            onClick={() => onPick(k)}
            style={{
              position: 'relative', zIndex: 1, flex: '1 1 0', minWidth: 0, height: compact ? 40 : size.tabH,
              border: 0, borderRadius: '12px', cursor: 'pointer', font: 'inherit', background: 'transparent',
              display: 'flex', flexDirection: column ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: column ? '3px' : '9px',
              color: on ? colors.text : colors.muted, transition: `color ${MOTION.label}ms ease`,
            }}>
            {compact
              ? <><span style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '22px', lineHeight: 1, color: on ? colors.accent : colors.muted, transition: `color ${MOTION.label}ms ease` }}>{counts[k]}</span>
                  <span style={{ fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{TABS[k].label}</span></>
              : <><Icon kind={k} color={on ? colors.accent : colors.muted} size={size.icon} />
                  <span style={{ fontSize: size.tabFont, fontWeight: 500 }}>{TABS[k].label} <span style={{ opacity: 0.7 }}>{counts[k]}</span></span></>}
          </button>
        );
      })}
    </div>
  );
}

// ── The component ───────────────────────────────────────────────────────────
export default function LexicalResults({
  query, courses = [], chapters = [], sections = [], topicsCount = 0, topicsContent = null,
  onOpenTopic, isMobile = false, isTablet = false, isDesktop = true, sidebarOpen = true,
}) {
  const navigate = useNavigate();
  const { colors, isLight } = useTheme();
  const { profile, progressByVideo } = useAuth();
  const currentTier = profile?.currentTier || 'free';
  const size = SIZES[isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'];

  const lists = { sections, chapters, courses };
  const counts = { topics: topicsCount, sections: sections.length, chapters: chapters.length, courses: courses.length };
  const visible = TAB_ORDER.filter((k) => counts[k] > 0);

  const [chosen, setChosen] = useState(null);
  const active = chosen && counts[chosen] > 0 ? chosen : (TAB_PRIORITY.find((k) => counts[k] > 0) || null);
  const [shown, setShown] = useState({ sections: PAGE, chapters: PAGE, courses: PAGE });
  // Direction of the last switch, so the new results slide in from where the
  // selection moved: 'right' when moving to a tab further right, 'left' otherwise.
  // null on first render → no entrance animation on page load.
  const [dir, setDir] = useState(null);

  // ── measure the columns so the trails aim at their real centres ──
  const colsRef = useRef(null);
  const [geo, setGeo] = useState({ width: 0, targets: [] });
  useLayoutEffect(() => {
    const el = colsRef.current;
    if (!el) return undefined;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const targets = Array.from(el.children).map((c) => {
        const num = c.firstElementChild || c;                  // the big number
        const r = num.getBoundingClientRect();
        return { x: Math.round((r.left - box.left + r.width / 2) * 10) / 10, y: Math.round((r.top - box.top + r.height / 2) * 10) / 10 };
      });
      const key = targets.map((t) => t.x + ':' + t.y).join();
      setGeo((g) => (g.width === box.width && g.key === key ? g : { width: box.width, targets, key }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visible.length, isMobile, isTablet]);

  // ── the page's tab strip: watched for the collapse bar, and measured so the bar's
  //    strip sits exactly where the page's strip is (same left edge and width) ──
  const tabsRef = useRef(null);
  const resultsRef = useRef(null);
  // HEIGHT HOLD. Switching to a shorter tab would shorten the page under a reader
  // who is scrolled down; the browser would then snap the scroll position up and
  // everything on screen would jump. So on a switch the results area keeps its
  // height (min-height) and gives the space back once the reader has scrolled up
  // far enough that releasing it can't move anything.
  // Written straight to the element (no re-render on every scroll event).
  const holdRef = useRef(null);   // { h, below }
  // CROSSFADE. On a switch, a snapshot of exactly what was on screen (expanded
  // sections, scrolled rows and all) is laid over the results area and fades out
  // while the new results float in, so the area is never empty.
  const ghostRef = useRef(null);
  // The first results of a search cascade in as part of the opening sequence;
  // after the first switch, cascades start almost immediately.
  const introPendingRef = useRef(INTRO);
  const [strip, setStrip] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const el = tabsRef.current;
    if (!isDesktop || !el) { setCollapsed(false); return undefined; }
    const measure = () => {
      const r = el.getBoundingClientRect();
      setStrip((s) => (s && s.left === r.left && s.width === r.width ? s : { left: r.left, width: r.width }));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    const io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([e]) => { measure(); setCollapsed(!e.isIntersecting && e.boundingClientRect.top < 0); })
      : null;
    if (io) io.observe(el);
    return () => { if (ro) ro.disconnect(); if (io) io.disconnect(); window.removeEventListener('resize', measure); };
  }, [isDesktop, visible.length]);

  // After each switch: keep the full held height until the reader has scrolled up
  // far enough that the new tab's own content reaches the bottom of the screen, then
  // release it in ONE step. At that moment the page is already tall enough for the
  // current scroll position, so releasing moves nothing. (No partial shrinking: a
  // step-by-step release with zero slack fed back into the browser's own scroll
  // adjustments and slid the page anyway.)
  useEffect(() => {
    const el = resultsRef.current;
    if (!el || !holdRef.current) return undefined;
    const check = () => {
      const hold = holdRef.current;
      if (!hold) return;
      const sc = pageScroller();
      const content = el.querySelector('[data-lx-content]');
      const natural = content ? content.offsetHeight : 0;
      const need = scrollerTop(sc) + sc.clientHeight - el.getBoundingClientRect().top - hold.below;
      if (need <= natural) { el.style.minHeight = ''; holdRef.current = null; stop(); }
    };
    const stop = () => { window.removeEventListener('scroll', check, SCROLL_OPTS); window.removeEventListener('resize', check); };
    window.addEventListener('scroll', check, SCROLL_OPTS);
    window.addEventListener('resize', check);
    check();
    return stop;
  }, [active]);

  if (!visible.length) return null;   // the page shows its own "No videos match" message

  const total = visible.reduce((n, k) => n + counts[k], 0);
  const activeIdx = visible.indexOf(active);
  const lockStyle = { color: isLight ? '#FFFFFF' : colors.text, bg: isLight ? 'rgba(0,0,0,0.62)' : 'rgba(0,0,0,0.55)' };
  const scale = isMobile ? 0.7 : 1;
  const edge = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const go = (path) => navigate(path);

  // Every way of changing tab (page tabs, bar tabs, columns, arrow keys) goes here,
  // so the slide direction is always right.
  const selectTab = (k) => {
    if (!k || k === active) return;
    const el = resultsRef.current;
    if (el) {
      const sc = pageScroller();
      const h = el.offsetHeight;
      const contentTop = el.getBoundingClientRect().top - scrollerTop(sc) + sc.scrollTop;
      holdRef.current = { h, below: sc.scrollHeight - (contentTop + h) };
      el.style.minHeight = h + 'px';
    }
    const movingRight = visible.indexOf(k) > visible.indexOf(active);
    const live = el && el.querySelector('[data-lx-content]');
    const holder = ghostRef.current;
    if (live && holder && !prefersReducedMotion()) {
      const ghost = live.cloneNode(true);
      ghost.removeAttribute('data-lx-content');
      ghost.querySelectorAll('video').forEach((v) => v.remove());          // never play in the snapshot
      ghost.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      ghost.className = 'lx-ghost lx-out-' + (movingRight ? 'left' : 'right');
      ghost.inert = true;                                                    // no focus, no clicks, no screen reader
      ghost.querySelectorAll('button, a, [tabindex]').forEach((n) => n.setAttribute('tabindex', '-1'));
      holder.replaceChildren(ghost);
      // keep horizontally scrolled rows exactly where they were (only the topic rows
      // scroll sideways — copying just those keeps the snapshot cheap)
      const from = live.querySelectorAll('.video-row-scroll'), to = ghost.querySelectorAll('.video-row-scroll');
      from.forEach((n, i) => { if (n.scrollLeft && to[i]) to[i].scrollLeft = n.scrollLeft; });
      const clear = () => { if (ghost.isConnected) ghost.remove(); };
      ghost.addEventListener('animationend', clear, { once: true });
      setTimeout(clear, MOTION.out + 250);
    }
    introPendingRef.current = false;
    setDir(movingRight ? 'right' : 'left');
    setChosen(k);
  };

  const pickFromBar = (k) => {
    selectTab(k);
    const res = resultsRef.current;
    if (!res) return;
    const barEl = document.querySelector('.lx-bar');
    const barH = barEl ? barEl.offsetHeight : 0;
    const sc = pageScroller();
    const top = res.getBoundingClientRect().top - scrollerTop(sc) + sc.scrollTop - barH - 12;
    sc.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  // arrow keys move between tabs (the tabs are the accessible control)
  const onTabKey = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = visible.indexOf(active);
    const next = visible[(i + (e.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length];
    selectTab(next);
    const btn = tabsRef.current && tabsRef.current.querySelector(`[data-tab="${next}"]`);
    if (btn) btn.focus();
  };

  const headline = () => (
    <>{total} {total === 1 ? 'Result' : 'Results'} for <span style={{ color: colors.accent }}>“{query}”</span></>
  );
  // TODO(LLM): the headline wording (and the search terms behind it) will come from
  // the language model later; this plain lexical headline is the placeholder.

  // Stagger for a card/row at position i. The first chunk of a list waits for the
  // opening sequence (or starts almost at once after a switch); "Show more" chunks
  // cascade on their own. Values depend only on position, so a card's delay never
  // changes while it is on screen.
  const cascade = (i) => {
    const base = i < PAGE ? (introPendingRef.current ? INTRO_AT.results : MOTION.out + MOTION.breath) : 0;
    return { '--i': (base + Math.min(i % PAGE, MOTION.staggerMax) * MOTION.stagger) + 'ms' };
  };

  const renderList = (k) => {
    const list = lists[k].slice(0, shown[k]);
    const more = lists[k].length - list.length;
    const common = { colors, isLight, isMobile, progressByVideo, lockStyle };
    return (
      <>
        {k === 'courses' && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? '12px' : '20px' }}>
            {list.map((e, i) => <div key={e.path + ':' + e.id} className="lx-item" style={cascade(i)}><CourseCard entry={e} onGo={go} {...common} /></div>)}
          </div>
        )}
        {k === 'chapters' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {list.map((e, i) => <div key={e.path + ':' + e.id} className="lx-item" style={cascade(i)}><ChapterCard entry={e} onGo={go} {...common} /></div>)}
          </div>
        )}
        {k === 'sections' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {list.map((e, i) => <div key={e.path + ':' + e.id} className="lx-item" style={cascade(i)}><SectionRow entry={e} currentTier={currentTier} onOpenTopic={onOpenTopic} {...common} /></div>)}
          </div>
        )}
        {more > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <button type="button" onClick={() => setShown((s) => ({ ...s, [k]: s[k] + PAGE }))}
              style={{ padding: '10px 22px', borderRadius: '22px', cursor: 'pointer', font: 'inherit', fontSize: '14px', background: 'transparent', color: colors.text, border: `1px solid ${edge}` }}>
              Show more ({Math.min(PAGE, more)} of {more} remaining)
            </button>
          </div>
        )}
      </>
    );
  };

  const barLeft = sidebarOpen ? SIDEBAR_W : 0;
  const stripProps = { keys: visible, active, counts, size, colors, isLight, edge, isMobile };

  return (
    <div className={'lx-root' + (INTRO ? ' lx-intro' : '')}>
      {/* Headline */}
      <h1 className="lx-rise" style={{ '--d': INTRO_AT.headline + 'ms', margin: `${size.headTop}px 0 0`, textAlign: 'center', fontFamily: '"Cormorant Garamond", serif', fontWeight: 500,
        fontSize: size.headline + 'px', lineHeight: 1.05, color: colors.text, overflowWrap: 'anywhere' }}>
        {headline()}
      </h1>

      {/* Headline → columns */}
      <div style={{ marginTop: isMobile ? 4 : 8 }}>
        <HeadlineTrails width={geo.width} targets={geo.targets} activeIdx={activeIdx} colors={colors}
          height={size.trailH} scale={scale} />
      </div>

      {/* Columns (mouse targets; the tabs below are the accessible control).
          Padding 7px = the tab strip's 6px padding + 1px border, so every column
          sits exactly over its tab. */}
      <div ref={colsRef} aria-hidden="true" style={{ display: 'flex', gap: '6px', padding: '0 7px' }}>
        {visible.map((k) => {
          const on = k === active;
          return (
            <div key={k} className="lx-appear" onClick={() => selectTab(k)} style={{ '--d': (INTRO_AT.columns + visible.indexOf(k) * INTRO_AT.columnStep) + 'ms', flex: '1 1 0', minWidth: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <span className="lx-fade" style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, lineHeight: 0.9, fontSize: size.num + 'px', color: on ? colors.accent : colors.muted, opacity: on ? 1 : 0.75 }}>{counts[k]}</span>
              <span className="lx-fade" style={{ marginTop: size.labelGap, fontSize: size.label + 'px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: on ? colors.text : colors.muted }}>{TABS[k].label}</span>
              {size.desc > 0 && <span style={{ marginTop: 3, fontSize: size.desc + 'px', color: colors.muted }}>{TABS[k].desc}</span>}
              <div style={{ marginTop: size.dropGap }}><DropTrail on={on} colors={colors} height={size.dropH} scale={scale} /></div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="lx-rise" style={{ '--d': INTRO_AT.tabs + 'ms', marginTop: 8 }}>
        <TabStrip {...stripProps} compact={false} onPick={selectTab} onKeyDown={onTabKey} stripRef={tabsRef} />
      </div>

      {/* Results for the selected tab. The ghost layer (behind) holds the outgoing
          snapshot during a switch; the live content (keyed by tab) floats in from the
          direction the selection moved, and its cards cascade. */}
      <div ref={resultsRef} role="tabpanel" style={{ position: 'relative', marginTop: isMobile ? 20 : 32 }}>
        <div ref={ghostRef} aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 0, pointerEvents: 'none' }} />
        <div key={active} data-lx-content="" className={dir ? 'lx-in-' + dir : undefined} style={{ position: 'relative' }}>
          {active === 'topics'
            ? React.Children.toArray(topicsContent).map((row, i) => <div key={row.key != null ? row.key : i} className="lx-item" style={cascade(i)}>{row}</div>)
            : renderList(active)}
        </div>
      </div>

      {/* Desktop only: compact bar once the tabs have scrolled away. Its strip takes
          the page strip's exact left edge and width, and starts below the sidebar
          toggle, so it lines up with the tabs it replaces and never sits under the
          toggle. */}
      {isDesktop && strip && (
        <div className={'lx-bar' + (collapsed ? ' lx-bar--on' : '')} aria-hidden={!collapsed} style={{
          position: 'fixed', top: 0, right: 0, left: barLeft, zIndex: 100,
          height: BAR_STRIP_TOP + 40 + 14 + 10,
          background: colors.bg, borderBottom: `1px solid ${edge}`, boxShadow: '0 14px 30px -18px rgba(0,0,0,0.6)',
          // glides in and out; hidden from clicks, Tab and screen readers when away
          transform: collapsed ? 'none' : 'translateY(-104%)', opacity: collapsed ? 1 : 0,
          visibility: collapsed ? 'visible' : 'hidden', pointerEvents: collapsed ? 'auto' : 'none',
          transition: `transform ${MOTION.bar}ms ${GLIDE}, opacity ${MOTION.bar}ms ease, visibility 0s linear ${collapsed ? 0 : MOTION.bar}ms`,
        }}>
          <div style={{ position: 'absolute', top: 0, left: strip.left - barLeft, width: strip.width }}>
            <div style={{ height: BAR_STRIP_TOP, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: '"Cormorant Garamond", serif', fontSize: '22px', color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {/* one inline span: in a flex container, loose text and the coloured
                  quote would become separate flex items and lose the space between them */}
              <span>{headline()}</span>
            </div>
            <TabStrip {...stripProps} compact onPick={pickFromBar} />
          </div>
        </div>
      )}

      <style>{`
        .lx-fade { transition: fill ${MOTION.fade}ms ease, opacity ${MOTION.fade}ms ease, color ${MOTION.fade}ms ease; }

        /* opening sequence (each element's --d sets its moment) */
        .lx-intro .lx-rise   { animation: lxRise 900ms ${GLIDE} backwards; animation-delay: var(--d, 0ms); }
        .lx-intro .lx-appear { animation: lxAppear 600ms ease-out backwards; animation-delay: var(--d, 0ms); }
        .lx-intro .lx-dot    { animation: lxAppear 400ms ease backwards;    animation-delay: var(--d, 0ms); }
        @keyframes lxRise   { from { opacity: 0; transform: translateY(12px); } }
        @keyframes lxAppear { from { opacity: 0; } }

        /* cards and rows settle one after another */
        .lx-item { animation: lxItem ${MOTION.item}ms ${GLIDE} backwards; animation-delay: var(--i, 0ms); }
        @keyframes lxItem { from { opacity: 0; transform: translateY(8px); } }

        /* switching — fade through: the old results (a snapshot) leave first; after a
           short breath the new results arrive. They are never on screen together. */
        .lx-out-left  { animation: lxOutLeft  ${MOTION.out}ms cubic-bezier(0.4, 0, 1, 1) forwards; }
        .lx-out-right { animation: lxOutRight ${MOTION.out}ms cubic-bezier(0.4, 0, 1, 1) forwards; }
        @keyframes lxOutLeft  { to { opacity: 0; transform: translateX(-8px); } }
        @keyframes lxOutRight { to { opacity: 0; transform: translateX(8px); } }
        .lx-in-right, .lx-in-left { transform-origin: 50% 0; }
        .lx-in-right { animation: lxInRight ${MOTION.in}ms ${GLIDE} backwards; animation-delay: ${MOTION.out + MOTION.breath}ms; }
        .lx-in-left  { animation: lxInLeft  ${MOTION.in}ms ${GLIDE} backwards; animation-delay: ${MOTION.out + MOTION.breath}ms; }
        @keyframes lxInRight { from { opacity: 0; transform: translateX(12px) scale(0.985); } }
        @keyframes lxInLeft  { from { opacity: 0; transform: translateX(-12px) scale(0.985); } }
        .lx-ghost * { animation: none !important; transition: none !important; }

        /* a section's lessons cascade as it opens */
        .lx-lessons-open .lx-lesson { animation: lxItem 600ms ${GLIDE} backwards; animation-delay: var(--i, 0ms); }

        /* cards lift gently under the pointer */
        @media (hover: hover) {
          .lx-card { transition: transform 500ms ${GLIDE}, box-shadow 500ms ease; }
          .lx-card:hover { transform: translateY(-2px); box-shadow: 0 0 0 1px ${colors.accent}66, 0 18px 36px -22px rgba(0,0,0,0.55); }
        }

        @media (prefers-reduced-motion: reduce) {
          .lx-root, .lx-root *, .lx-bar { animation: none !important; transition: none !important; }
          .lx-root .lx-in-right, .lx-root .lx-in-left { animation: lxFadeOnly 150ms linear !important; }
          @keyframes lxFadeOnly { from { opacity: 0; } }
        }
      `}</style>
    </div>
  );
}
